/**
 * Typed client methods for the BEAM SmartBox surface.
 *
 * All shapes come from @beam/api-types (never re-declared here).
 * The `post`/`get` helpers from src/api/client.ts attach the Bearer token
 * automatically. Scope is derived from the JWT server-side — never
 * sent in the request body (mobile CLAUDE.md invariant 2; smartboxRouter
 * reads req.tenant.scope from middleware).
 *
 * Routes confirmed in beam-backend/src/api/smartboxRouter.ts:
 *   POST /api/sb/ingest
 *   POST /api/sb/batches/:batchId/files/:fileId/confirm
 *   POST /api/sb/batches/:batchId/files/:fileId/discard  → 204
 *   POST /api/sb/facts/:factId/correct
 *   POST /api/sb/assistant/ask                           → AssistantReply
 *   POST /api/sb/estimator/drafts                        → NewDraftResponse
 *   POST /api/sb/estimator/:draftId/turn                 → EstimatorReply
 *   POST /api/sb/estimator/:draftId/activate             → { ok: true, estimateId }
 *   GET  /api/sb/facts/:factId/provenance                → ProvenanceChain
 */

import { post, get } from './client';
import type {
  IngestRequest,
  IngestFile,
  BatchResult,
  ConfirmRequest,
  CommitResult,
  CorrectFactRequest,
  FactPatch,
  Turn,
  AssistantReply,
  EstimatorReply,
  NewDraftResponse,
  ProvenanceChain,
} from '@beam/api-types';
import {
  BatchResult as BatchResultSchema,
  CommitResult as CommitResultSchema,
  AssistantReply as AssistantReplySchema,
  EstimatorReply as EstimatorReplySchema,
  NewDraftResponse as NewDraftResponseSchema,
  ProvenanceChain as ProvenanceChainSchema,
} from '@beam/api-types';

/**
 * Upload one or more files for extraction.
 * Each file must already be base64-encoded (done by the capture screen
 * using expo-file-system readAsStringAsync with Base64 encoding).
 */
export async function ingest(files: IngestFile[]): Promise<BatchResult> {
  const body: IngestRequest = { files };
  const raw = await post<unknown>('/api/sb/ingest', body);
  return BatchResultSchema.parse(raw);
}

/**
 * Confirm one file's extracted records, creating committed FactRecords.
 * Returns a CommitResult whose committedRecords contain the FactRecord ids
 * needed for any subsequent correctFact calls.
 */
export async function confirmFile(
  batchId: string,
  fileId: string,
  submitterId: string,
): Promise<CommitResult> {
  const body: ConfirmRequest = { submitterId };
  const raw = await post<unknown>(
    `/api/sb/batches/${batchId}/files/${fileId}/confirm`,
    body,
  );
  return CommitResultSchema.parse(raw);
}

/**
 * Discard one file's extracted records. Returns void (204 No Content).
 */
export async function discardFile(
  batchId: string,
  fileId: string,
): Promise<void> {
  await post<unknown>(`/api/sb/batches/${batchId}/files/${fileId}/discard`);
}

/**
 * Correct a committed fact (audit row precedes the live update on the backend).
 * IMPORTANT ORDERING: correctFact applies to a COMMITTED FactRecord (id from
 * CommitResult.committedRecords). This can only be called after confirmFile
 * succeeds and the CommitResult is in hand. Corrections on pre-confirmation
 * pending records are not possible via this route.
 */
export async function correctFact(
  factId: string,
  patch: FactPatch,
  reason: string,
): Promise<{ ok: boolean; reprojected: boolean }> {
  const body: CorrectFactRequest = { patch, reason };
  return post<{ ok: boolean; reprojected: boolean }>(
    `/api/sb/facts/${factId}/correct`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Assistant (WO-25)
// ---------------------------------------------------------------------------

/**
 * Send a question to the SmartBox assistant in the context of a tenancy unit.
 *
 * tenancyUnitId: see ASSUMPTION note in AssistantPane — currently the session
 * user's organizationId is used as a fallback. Confirm the intended semantic
 * (jobId / estimateId vs organizationId) with the backend team before production.
 *
 * history: the caller maintains the Turn[] locally (AssistantPane state) and
 * passes the full conversation on every call so the backend is stateless.
 */
export async function assistantAsk(
  tenancyUnitId: string,
  question: string,
  history: Turn[],
): Promise<AssistantReply> {
  const raw = await post<unknown>('/api/sb/assistant/ask', {
    tenancyUnitId,
    question,
    history,
  });
  return AssistantReplySchema.parse(raw);
}

// ---------------------------------------------------------------------------
// Estimator (WO-25)
// ---------------------------------------------------------------------------

/**
 * Create a new estimator draft on the backend. Returns a draftId that the
 * caller passes to every subsequent estimatorTurn and estimatorActivate call.
 * The draftId is an opaque server-assigned UUID.
 */
export async function newEstimatorDraft(): Promise<NewDraftResponse> {
  const raw = await post<unknown>('/api/sb/estimator/drafts');
  return NewDraftResponseSchema.parse(raw);
}

/**
 * Advance the estimator draft with a user input turn.
 * The reply contains the evolving draft content and the draftId (echoed back
 * for convenience; callers should prefer the draftId obtained from newEstimatorDraft).
 */
export async function estimatorTurn(
  draftId: string,
  input: string,
): Promise<EstimatorReply> {
  const raw = await post<unknown>(`/api/sb/estimator/${draftId}/turn`, { input });
  return EstimatorReplySchema.parse(raw);
}

/**
 * Activate a completed draft against a tenancy unit + customer (design doc §4/§9).
 *
 * On the backend this now MATERIALIZES a BEAM estimate from the draft's line items
 * (createEstimateFromSmartboxDraft) against the chosen customer, lands it at
 * approval_status='draft', and returns its `estimateId` so the caller can navigate
 * straight to the native estimate view. SmartBox never owns pricing approval, markup,
 * e-signature, or send-for-signature — those are owner-gated operations in the BEAM
 * estimate lifecycle (WO-16 / CLAUDE.md invariant 6).
 *
 * tenancyUnitId: same assumption as assistantAsk — falls back to organizationId.
 * customerId: the customer the new estimate is created for (required by the backend).
 */
export async function estimatorActivate(
  draftId: string,
  tenancyUnitId: string,
  customerId: string,
): Promise<{ ok: boolean; estimateId: string }> {
  return post<{ ok: boolean; estimateId: string }>(`/api/sb/estimator/${draftId}/activate`, {
    tenancyUnitId,
    customerId,
  });
}

// ---------------------------------------------------------------------------
// Provenance (WO-25)
// ---------------------------------------------------------------------------

/**
 * Fetch the full provenance chain for a committed fact.
 * Read-only: the mobile app never mutates provenance records.
 */
export async function provenanceChain(factId: string): Promise<ProvenanceChain> {
  const raw = await get<unknown>(`/api/sb/facts/${factId}/provenance`);
  return ProvenanceChainSchema.parse(raw);
}
